import { programsApi } from "./api/program.api";
import { clearCatalog } from "./courseCatalog";
import { clearSuggestions } from "./requirementSuggestions";
import { applyFulfillmentToProgram } from "../utils/fulfillmentMerge";
import { CourseOption } from "../types/course.response";
import { ProgramFulfillmentResponse } from "../types/program-fulfillment.response";
import { ProgramSkeletonResponse, ProgramSummaryResponse } from "../types/program.response";

/**
 * requirement 切片编排器(渐进加载契约 §5.2 第 2 步)。
 *
 * 按 program 管理 skeleton + 已加载切片,对外派生"部分填充的 ProgramSummaryResponse"
 * 合并对象:requirements 取 skeleton 全量树(含节点 summaries),courses 为已到切片
 * 的并集。页面据 sliceStatus 决定渲染真实区块还是占位区块,组件树形状不变。
 *
 * 角色类似 courseCatalog.ts:模块级单例 + subscribe/version 供 useSyncExternalStore。
 * 引用稳定性约束(§5.4):快照与合并对象按 program 粒度缓存,仅在该 program 数据
 * 变化时生成新引用;切片合并不触碰 requirements 数组引用。
 */

export type SliceStatus = "idle" | "loading" | "loaded" | "error";

/**
 * 出队排序策略(可插拔,§3.1):给定待加载的 requirement id 与该 program 的
 * 合并对象,返回出队顺序。当前默认 = 展示顺序;远期 Home feed 的 ranker 在此替换。
 */
export type SliceOrderStrategy = (
    pendingIds: string[],
    program: ProgramSummaryResponse,
) => string[];

export interface ProgramSliceSnapshot {
    /** skeleton 的加载状态;各 requirement 切片状态见 sliceStatus */
    status: "loading" | "loaded" | "error";
    error?: string;
    /** 合并对象(§5.1)。status === "loaded" 时必有 */
    program?: ProgramSummaryResponse;
    /** per requirement.info.id,skeleton 到达时全量置 idle */
    sliceStatus: Record<string, SliceStatus>;
    /** 后端去范式化字段(§4.1),per requirement.info.id;不受 fulfillment patch 影响 */
    hasAvailableCourses: Record<string, boolean>;
}

export interface SliceOrchestratorConfig {
    fetchSkeleton: (programId: string) => Promise<ProgramSkeletonResponse>;
    fetchSlices: (programId: string, requirementIds: string[]) => Promise<ProgramSummaryResponse>;
    /** structureVersion 漂移整库失效时调用(生产 = clearCatalog) */
    onStructureInvalidated: () => void;
    /** 全局在途切片请求上限(§3.1 并发预算),默认 4 */
    maxInflight?: number;
    /** program 内合并为一个请求的 requirement 数(§3.1),默认 3 */
    batchSize?: number;
    orderStrategy?: SliceOrderStrategy;
}

interface ProgramEntry {
    status: "loading" | "loaded" | "error";
    error?: string;
    /** 合并对象本体:fulfillment patch 与切片合并都落在这里 */
    program?: ProgramSummaryResponse;
    hasAvailableCourses: Record<string, boolean>;
    sliceStatus: Map<string, SliceStatus>;
    /** 待出队的 requirement id(已去重;顺序在出队时由策略决定) */
    queue: string[];
    /** 视口预取等提升过优先级的 id,出队时排在策略序前部 */
    prioritized: Set<string>;
    /** 历史上被请求过的全部 id:失效重拉 skeleton 后据此重新入队 */
    requested: Set<string>;
    /** 已应用的最大用户状态版本(契约 §3.3-2);后端未落地时 undefined,由页面请求序号兜底 */
    stateVersion?: number;
    snapshot: ProgramSliceSnapshot;
}

/** 默认策略:skeleton 的 requirements 展示顺序 */
export const displayOrderStrategy: SliceOrderStrategy = (pendingIds, program) => {
    const order = new Map<string, number>();
    program.requirements.forEach((r, i) => order.set(r.info.id, i));
    return pendingIds.slice().sort(
        (a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER),
    );
};

export function createSliceOrchestrator(config: SliceOrchestratorConfig) {
    const maxInflight = config.maxInflight ?? 4;
    const batchSize = config.batchSize ?? 3;
    const orderStrategy = config.orderStrategy ?? displayOrderStrategy;

    const entries = new Map<string, ProgramEntry>();
    const listeners = new Set<() => void>();
    let version = 0;
    let inflightCount = 0;
    /** round-robin 游标:多 program 都有积压时轮流出批,避免首个 program 吃满预算 */
    let pumpCursor = 0;

    function notify() {
        version++;
        listeners.forEach(listener => listener());
    }

    function rebuildSnapshot(entry: ProgramEntry) {
        const sliceStatus: Record<string, SliceStatus> = {};
        entry.sliceStatus.forEach((status, id) => { sliceStatus[id] = status; });
        entry.snapshot = {
            status: entry.status,
            error: entry.error,
            program: entry.program,
            sliceStatus,
            hasAvailableCourses: entry.hasAvailableCourses,
        };
    }

    function freshEntry(previous?: ProgramEntry): ProgramEntry {
        const entry: ProgramEntry = {
            status: "loading",
            hasAvailableCourses: {},
            sliceStatus: new Map(),
            queue: [],
            prioritized: new Set(previous ? Array.from(previous.prioritized) : []),
            requested: new Set(previous ? Array.from(previous.requested) : []),
            snapshot: undefined as unknown as ProgramSliceSnapshot,
        };
        rebuildSnapshot(entry);
        return entry;
    }

    /**
     * 在途响应的归属校验:每次重置都创建新 entry 对象,旧请求回调里的
     * entry 引用对不上即为过期批次(切 college、失效重拉),直接丢弃。
     */
    function isStale(programId: string, entry: ProgramEntry): boolean {
        return entries.get(programId) !== entry;
    }

    function loadSkeleton(programId: string, entry: ProgramEntry) {
        config.fetchSkeleton(programId)
            .then(skeleton => {
                if (isStale(programId, entry)) return;
                entry.status = "loaded";
                entry.error = undefined;
                entry.program = { ...skeleton, courses: {} };
                entry.hasAvailableCourses = {};
                entry.sliceStatus = new Map();
                skeleton.requirements.forEach(r => {
                    entry.hasAvailableCourses[r.info.id] = r.info.hasAvailableCourses;
                    entry.sliceStatus.set(r.info.id, "idle");
                });
                // 之前请求过的切片(首次加载为空;失效重拉时为已消费集合)与新结构求交后重新入队
                entry.queue = Array.from(entry.requested).filter(id => entry.sliceStatus.has(id));
                rebuildSnapshot(entry);
                notify();
                pump();
            })
            .catch(err => {
                if (isStale(programId, entry)) return;
                entry.status = "error";
                entry.error = err instanceof Error ? err.message : "Failed to load";
                rebuildSnapshot(entry);
                notify();
            });
    }

    /** 取下一批要发的切片:round-robin 选 program,程序内按策略序 + 优先级取 batchSize 个 */
    function nextBatch(): { programId: string; entry: ProgramEntry; ids: string[] } | null {
        const programIds = Array.from(entries.keys());
        for (let i = 0; i < programIds.length; i++) {
            const programId = programIds[(pumpCursor + i) % programIds.length];
            const entry = entries.get(programId)!;
            if (!entry.program || entry.queue.length === 0) continue;
            pumpCursor = (pumpCursor + i + 1) % programIds.length;

            const ordered = orderStrategy(entry.queue.slice(), entry.program);
            const batch = ordered.filter(id => entry.prioritized.has(id))
                .concat(ordered.filter(id => !entry.prioritized.has(id)))
                .slice(0, batchSize);
            entry.queue = entry.queue.filter(id => batch.indexOf(id) === -1);
            return { programId, entry, ids: batch };
        }
        return null;
    }

    function pump() {
        while (inflightCount < maxInflight) {
            const work = nextBatch();
            if (!work) return;
            fire(work.programId, work.entry, work.ids);
        }
    }

    function fire(programId: string, entry: ProgramEntry, ids: string[]) {
        ids.forEach(id => entry.sliceStatus.set(id, "loading"));
        rebuildSnapshot(entry);
        notify();
        inflightCount++;
        config.fetchSlices(programId, ids)
            .then(slice => {
                if (isStale(programId, entry)) return;
                if (slice.structureVersion !== entry.program!.structureVersion) {
                    // 恰逢管线更新(§6):丢弃该切片,整 program 重拉 skeleton 兜底
                    invalidateProgram(programId);
                    return;
                }
                // 合并幂等(§4.2):courses 按 key 合并,后到覆盖;requirements 引用不动(§5.4)
                entry.program = {
                    ...entry.program!,
                    courses: { ...entry.program!.courses, ...slice.courses },
                };
                ids.forEach(id => entry.sliceStatus.set(id, "loaded"));
                rebuildSnapshot(entry);
                notify();
            })
            .catch(err => {
                if (isStale(programId, entry)) return;
                console.error(
                    `[programSlices] slice fetch failed (program=${programId}, requirements=${ids.join(",")})`,
                    err,
                );
                ids.forEach(id => entry.sliceStatus.set(id, "error"));
                rebuildSnapshot(entry);
                notify();
            })
            .finally(() => {
                inflightCount--;
                pump();
            });
    }

    /** 确保 program 的 skeleton 已加载/在途;error 态调用 = 重试 */
    function ensureProgram(programId: string): void {
        const existing = entries.get(programId);
        if (existing && existing.status !== "error") return;
        const entry = freshEntry(existing);
        entries.set(programId, entry);
        notify();
        loadSkeleton(programId, entry);
    }

    /** 请求加载切片。loaded/loading 自动去重;error 态的 id 重新入队(重试) */
    function requestSlices(programId: string, requirementIds: string[]): void {
        const entry = entries.get(programId);
        if (!entry) return;
        requirementIds.forEach(id => entry.requested.add(id));
        if (!entry.program) return; // skeleton 到达时按 requested 统一入队
        let retried = false;
        for (const id of requirementIds) {
            const status = entry.sliceStatus.get(id);
            if (status === undefined) continue; // 不在当前结构中
            if (status === "loaded" || status === "loading") continue;
            if (status === "error") {
                entry.sliceStatus.set(id, "idle");
                retried = true;
            }
            if (entry.queue.indexOf(id) === -1) entry.queue.push(id);
        }
        if (retried) {
            rebuildSnapshot(entry);
            notify();
        }
        pump();
    }

    /** 视口预取(§5.2 第 3 步):提升出队优先级,未请求过的顺带入队 */
    function prioritize(programId: string, requirementIds: string[]): void {
        const entry = entries.get(programId);
        if (!entry) return;
        requirementIds.forEach(id => entry.prioritized.add(id));
        requestSlices(programId, requirementIds);
    }

    /**
     * 整 program 失效(切片/fulfillment 的 structureVersion 漂移):
     * 清切片与课程 catalog、作废在途批次、重拉 skeleton,已请求切片随后自动重新入队。
     */
    function invalidateProgram(programId: string): void {
        const existing = entries.get(programId);
        if (!existing) return;
        config.onStructureInvalidated();
        const entry = freshEntry(existing);
        entries.set(programId, entry);
        notify();
        loadSkeleton(programId, entry);
    }

    /**
     * 改课后的轻量刷新:fulfillment patch 打在合并对象上。
     * 节点树在 skeleton 中完整;userStates 只落在已加载切片的课程上(§3.2)。
     * 结构漂移(返回 null)→ 整 program 失效重拉。
     */
    function applyFulfillment(
        programId: string,
        fulfillment: ProgramFulfillmentResponse,
        userItems: CourseOption[],
    ): void {
        const entry = entries.get(programId);
        if (!entry?.program) return;
        // 乱序旧响应(契约 §3.3-2):stateVersion 小于等于已应用值 → 丢弃
        if (
            fulfillment.stateVersion != null &&
            entry.stateVersion != null &&
            fulfillment.stateVersion <= entry.stateVersion
        ) return;
        const merged = applyFulfillmentToProgram(entry.program, fulfillment, userItems);
        if (!merged) {
            invalidateProgram(programId);
            return;
        }
        if (fulfillment.stateVersion != null) entry.stateVersion = fulfillment.stateVersion;
        entry.program = merged;
        rebuildSnapshot(entry);
        notify();
    }

    /** 移除 program(切 college / 登出):在途批次到达时因 entry 替换被丢弃 */
    function clearProgram(programId: string): void {
        if (entries.delete(programId)) notify();
    }

    function clearAll(): void {
        if (entries.size === 0) return;
        entries.clear();
        notify();
    }

    function getSnapshot(programId: string): ProgramSliceSnapshot | undefined {
        return entries.get(programId)?.snapshot;
    }

    /** skeleton 已就绪的 programId:mutation 请求的 ?fulfillment= 取值(契约 §4.3) */
    function getLoadedProgramIds(): string[] {
        return Array.from(entries.entries())
            .filter(([, entry]) => entry.status === "loaded")
            .map(([programId]) => programId);
    }

    function subscribe(listener: () => void): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }

    /** 供 useSyncExternalStore 使用:任一 program 数据变化时递增 */
    function getVersion(): number {
        return version;
    }

    return {
        ensureProgram,
        requestSlices,
        prioritize,
        applyFulfillment,
        invalidateProgram,
        clearProgram,
        clearAll,
        getSnapshot,
        getLoadedProgramIds,
        subscribe,
        getVersion,
    };
}

export type SliceOrchestrator = ReturnType<typeof createSliceOrchestrator>;

/** 生产单例:Home / Program 页共享 */
export const programSlices = createSliceOrchestrator({
    fetchSkeleton: id => programsApi.getProgramSkeleton(id),
    fetchSlices: (id, requirementIds) => programsApi.getProgramRequirements(id, requirementIds),
    // 管线更新可能同时改了内涵谓词,推荐结果一并作废
    onStructureInvalidated: () => {
        clearCatalog();
        clearSuggestions();
    },
});
